import React, { useState, useEffect } from 'react'
import { FaEdit } from 'react-icons/fa'
import { MdDeleteForever } from 'react-icons/md'
import { GoPlus } from 'react-icons/go'
import * as api from '../utils/api/apiClient'
import { toast } from 'react-toastify'
import { Loading } from '../components/Loading'

const LeavePolicy = () => {
  const [isOpen, setIsOpen] = useState(false)
  const [isDelete, setIsDelete] = useState(false)
  const [isUpdate, setIsUpdate] = useState(false)
  const [loading, setLoading] = useState(false)
  const [leaveData, setLeaveData] = useState([])
  const [type, setType] = useState('')
  const [formData, setFormData] = useState({
    leave_type: '',
    max_leaves_per_year: '',
  })

  // API functions remain the same
  const EditLeave = async (Type, payload) => {
    try {
      await api.updateLeavePolicy(Type, payload)
      getLeave()
      toast.success('Leave policy Updated successfully')
    } catch (error) {
      if (error?.response?.data?.message) {
        toast.error(error?.response?.data?.message)
      }
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      const payload = {
        leave_type: formData.leave_type,
        max_leaves_per_year: parseInt(formData.max_leaves_per_year, 10),
      }
      await addLeave(payload)
      setIsOpen(false)
      setFormData({ leave_type: '', max_leaves_per_year: '' })
    } catch (error) {
      console.error(error)
    }
  }

  const addLeave = async (payload) => {
    try {
      await api.addLeavePolicy(payload)
      getLeave()
      toast.success('Leave Added Successfully')
    } catch (error) {
      if (error?.response?.data?.message) {
        toast.error(error?.response?.data?.message)
      }
    }
  }

  const deleteLeave = async (deleteType) => {
    try {
      await api.deleteLeavePolicy(deleteType)
      getLeave()
      toast.success('Leave policy deleted successfully')
    } catch (error) {
      if (error?.response?.data?.message) {
        toast.error(error?.response?.data?.message)
      }
    }
  }

  const getLeave = async () => {
    try {
      const res = await api.getLeavePolicy()
      setLeaveData(res.data)
    } catch (error) {
      if (error?.response?.data?.message) {
        toast.error(error?.response?.data?.message)
      }
    }
  }

  useEffect(() => {
    getLeave()
    setTimeout(() => {
      setLoading(true)
    }, 400)
  }, [])

  const getLeaveTypeLabel = (type) => {
    const types = {
      cl: 'Casual Leave (CL)',
      ml: 'Medical Leave (ML)',
      el: 'Earned Leave (EL)',
    }
    return types[type] || type
  }

  return (
    <>
      {!loading && <Loading />}
      <div className='min-h-screen bg-gradient-to-br from-cream to-white p-4 md:p-6 lg:p-8'>
        <div className='max-w-7xl mx-auto'>
          <div className='flex flex-col md:flex-row justify-between items-center mb-8 gap-4'>
            <h1 className='text-2xl md:text-3xl text-brown font-bold'>
              Leave Policy
            </h1>
            <button
              onClick={() => setIsOpen(true)}
              className='bg-tan p-2 flex items-center gap-2 text-white hover:bg-gold cursor-pointer rounded-md'
            >
              <GoPlus size={20} className='animate-pulse' /> Add Leave
            </button>
          </div>

          <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6'>
            {leaveData.map((leave) => (
              <div
                key={leave.id}
                className='bg-white rounded-xl shadow-md hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100 overflow-hidden'
              >
                <div className='bg-gradient-to-r from-tan/10 to-gold/10 p-4'>
                  <h2 className='text-xl font-bold text-tan'>
                    {getLeaveTypeLabel(leave.leave_type)}
                  </h2>
                </div>
                <div className='p-6'>
                  <div className='flex items-center justify-between mb-6'>
                    <p className='text-muted_green text-lg'>Leaves Per Year</p>
                    <span className='text-2xl font-bold text-brown'>
                      {leave.max_leaves_per_year}
                    </span>
                  </div>
                  <div className='flex justify-end gap-3'>
                    <button
                      onClick={() => {
                        setIsUpdate(true)
                        setType(leave.leave_type)
                      }}
                      className='flex items-center gap-2 px-4 py-2 rounded-lg bg-olive text-white hover:bg-muted_green transition-colors duration-200'
                    >
                      <FaEdit size={16} /> Edit
                    </button>
                    <button
                      onClick={() => {
                        setIsDelete(true)
                        setType(leave.leave_type)
                      }}
                      className='flex items-center gap-2 px-4 py-2 rounded-lg bg-tan text-white hover:bg-terracotta transition-colors duration-200'
                    >
                      <MdDeleteForever size={16} /> Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Modal Backdrop - shared by all modals */}
          {(isOpen || isDelete || isUpdate) && (
            <div className='fixed inset-0 bg-black/50 backdrop-blur-sm z-40 transition-opacity duration-300' />
          )}

          {/* Add Leave Modal */}
          {isOpen && (
            <div className='fixed inset-0 z-50 flex items-center justify-center p-4'>
              <div className='bg-white rounded-xl shadow-2xl w-full max-w-md transform transition-all duration-300 scale-100'>
                <div className='p-6'>
                  <h2 className='text-2xl font-bold text-brown mb-6'>
                    Add Leave Policy
                  </h2>
                  <form onSubmit={handleSubmit} className='space-y-4'>
                    <div>
                      <label className='block text-gray-700 text-sm font-bold mb-2'>
                        Leave Type
                      </label>
                      <select
                        value={formData.leave_type}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            leave_type: e.target.value,
                          })
                        }
                        className='w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-tan/50 transition-all duration-200'
                        required
                      >
                        <option value=''>Select Leave Type</option>
                        <option value='cl'>Casual Leave (CL)</option>
                        <option value='ml'>Medical Leave (ML)</option>
                        <option value='el'>Earned Leave (EL)</option>
                      </select>
                    </div>
                    <div>
                      <label className='block text-gray-700 text-sm font-bold mb-2'>
                        Maximum Leaves Per Year
                      </label>
                      <input
                        type='number'
                        value={formData.max_leaves_per_year}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            max_leaves_per_year: e.target.value,
                          })
                        }
                        className='w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-tan/50 transition-all duration-200'
                        required
                        min='1'
                      />
                    </div>
                    <div className='flex justify-end gap-4 pt-4'>
                      <button
                        type='button'
                        onClick={() => setIsOpen(false)}
                        className='px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors duration-200'
                      >
                        Cancel
                      </button>
                      <button
                        type='submit'
                        className='bg-gradient-to-r from-tan to-gold text-white px-6 py-2 rounded-lg hover:shadow-md transition-all duration-200'
                      >
                        Submit
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}

          {/* Delete Confirmation Modal */}
          {isDelete && (
            <div className='fixed inset-0 z-50 flex items-center justify-center p-4'>
              <div className='bg-white rounded-xl shadow-2xl w-full max-w-md transform transition-all duration-300'>
                <div className='p-6'>
                  <h2 className='text-2xl font-bold text-brown mb-4'>
                    Confirm Deletion
                  </h2>
                  <p className='text-gray-600 mb-6'>
                    Are you sure you want to delete this leave policy?
                  </p>
                  <div className='flex justify-end gap-4'>
                    <button
                      onClick={() => setIsDelete(false)}
                      className='px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors duration-200'
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => {
                        deleteLeave(type)
                        setIsDelete(false)
                      }}
                      className='bg-tan text-white px-6 py-2 rounded-lg hover:bg-terracotta transition-colors duration-200'
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Update Modal */}
          {isUpdate && (
            <div className='fixed inset-0 z-50 flex items-center justify-center p-4'>
              <div className='bg-white rounded-xl shadow-2xl w-full max-w-md transform transition-all duration-300'>
                <div className='p-6'>
                  <h2 className='text-2xl font-bold text-brown mb-6'>
                    Update Leave Policy
                  </h2>
                  <div className='mb-6'>
                    <label className='block text-gray-700 text-sm font-bold mb-2'>
                      Maximum Leaves Per Year
                    </label>
                    <input
                      type='number'
                      value={formData.max_leaves_per_year}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          max_leaves_per_year: e.target.value,
                        })
                      }
                      className='w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-tan/50 transition-all duration-200'
                      required
                      min='1'
                    />
                  </div>
                  <div className='flex justify-end gap-4'>
                    <button
                      onClick={() => {
                        setIsUpdate(false)
                        setFormData({ ...formData, max_leaves_per_year: '' })
                      }}
                      className='px-4 py-2 text-gray-600 hover:text-gray-800 transition-colors duration-200'
                    >
                      Cancel
                    </button>
                    <button
                      onClick={() => {
                        const payload = {
                          max_leaves_per_year: parseInt(
                            formData.max_leaves_per_year,
                            10
                          ),
                        }
                        EditLeave(type, payload)
                        setIsUpdate(false)
                        setFormData({ ...formData, max_leaves_per_year: '' })
                      }}
                      className='bg-tan to-gold text-white px-6 py-2 rounded-lg hover:shadow-md transition-all duration-200'
                    >
                      Update
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

export default LeavePolicy
